import { useQuery } from '@apollo/client'
import { Alert, Box, Button, Card, CircularProgress, Stack, Typography } from '@mui/material'
import { useCallback, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  GET_ORDER,
  isUnavailable,
  routeByName,
  toOrderDetailModel,
  type IGetOrder,
  type IGetOrderVariables,
  type ReorderLine,
} from '@commerce/core'
import { YALO_SEMANTIC } from '@commerce/tokens'
import { OrderStatusBadge, itemCountLabel } from '../components/OrderCard'
import { ActionBar, Screen, TopBar } from '../design'
import { useDemo } from '../demo'
import { deliveryOptionLabel } from '../checkout/deliveryOptions'
import { paymentMethodLabel } from '../checkout/paymentMethods'
import { unavailableLinesText } from '../text'
import { usePresentationContext } from '../hooks/usePresentationContext'
import { useReorderAvailability } from '../hooks/useReorderAvailability'
import { useUpdateCartQuantity } from '../hooks/useUpdateCartQuantity'

const pathTo = (name: 'orders' | 'cart', slug: string, sessionId: string): string =>
  routeByName(name).path.replace(':slug', slug).replace(':sessionId', sessionId)

const Row = ({ label, value }: { label: string; value: string }) => (
  <Stack direction="row" justifyContent="space-between" spacing={2}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600} sx={{ textAlign: 'right' }}>
      {value}
    </Typography>
  </Stack>
)

/**
 * One order.
 *
 * Two reads meet on this screen and the seam is deliberate. The order itself —
 * its lines, its date, its status, its total — comes from `order(id)`, scoped
 * to the session, so an id belonging to somebody else resolves to nothing.
 * Everything "Pedir de nuevo" *does* is a separate question: it writes the
 * order's lines into the session's actual cart through the same mutation the
 * catalog and cart pages use.
 *
 * Which means the two can disagree, and this is the one screen where they do. A
 * sku the order was placed with may have been delisted or gone out of stock
 * since, so the catalog is consulted and every line is resolved against it
 * before anything is written. The consultation stays lazy — the tap is what
 * pays for it, not the visit, because reading an order should not also cost a
 * whole catalog. When everything resolves, the tap adds it all and moves on; when
 * something does not, the tap stops, names the missing lines with the reason,
 * and a second, informed tap adds what is left — a shopper who gets a cart two
 * items short with no explanation has been lied to.
 *
 * The design replaces the whole cart with the order (`reference.html` line
 * 1600). This does not: it sets those lines to those quantities and leaves the
 * rest of the cart alone. Discarding a cart the shopper is in the middle of
 * building, with no warning and no undo, is not a thing to do for a convenience
 * action. `replace: true` still matters within those lines — repeating an order
 * twice sets 4 boxes, it does not accumulate to 8.
 */
export const OrderDetailPage = () => {
  const { slug = '', sessionId = '', orderId = '' } = useParams()
  const navigate = useNavigate()
  const presentation = usePresentationContext()
  const { distributor } = useDemo()
  const { updateQuantity, error: cartError } = useUpdateCartQuantity()

  const {
    data: orderData,
    loading: orderLoading,
    error: orderError,
  } = useQuery<IGetOrder, IGetOrderVariables>(GET_ORDER, {
    variables: { storefrontName: slug, sessionUid: sessionId, id: orderId },
  })

  const order = useMemo(
    () =>
      orderData?.order
        ? toOrderDetailModel(orderData.order, presentation, presentation.locale)
        : null,
    [orderData, presentation],
  )

  // Shared with the home's "Repetir mi último pedido": one resolver deciding
  // what an old order can still buy, so the two entry points cannot disagree
  // about the same sku.
  const { check, loading, error } = useReorderAvailability()

  // `null` until the shopper taps and the catalog answers. Everything below —
  // the warning, the button's label and whether it is withheld — reads from
  // this, so before the tap the page is just the order.
  const [checked, setChecked] = useState<ReorderLine[] | null>(null)

  const available = (checked ?? []).filter((line) => line.availability === 'available')
  const unavailable = (checked ?? []).filter(isUnavailable)

  const addToCartAndGo = useCallback(
    async (lines: ReorderLine[]) => {
      // One mutation per line, and deliberately still so. `prefillCart` would
      // batch them — the home's "Repetir mi último pedido" uses it for exactly
      // that — but it *adds*, and this screen sets: repeating an order twice
      // here means 4 boxes, not 8. Awaited so the cart page opens on the cart
      // these writes produced, not on a snapshot racing them.
      await Promise.all(lines.map((line) => updateQuantity(line.sku, line.quantity)))
      navigate(pathTo('cart', slug, sessionId))
    },
    [updateQuantity, navigate, slug, sessionId],
  )

  const reorder = useCallback(async () => {
    // Second tap: availability is known and on screen; add what survived.
    if (checked) {
      if (available.length > 0) await addToCartAndGo(available)
      return
    }

    // No answer, no action: the error alert names the failure and the button
    // stays armed for a retry.
    const lines = await check(order?.reorder ?? {})
    if (!lines) return
    setChecked(lines)

    // The whole order is still sellable — nothing to warn about, add it all.
    // Anything less stops here so the shopper reads what is missing first.
    const sellable = lines.filter((line) => line.availability === 'available')
    if (sellable.length === lines.length && sellable.length > 0) await addToCartAndGo(sellable)
  }, [checked, available, addToCartAndGo, check, order])

  if (orderLoading && !order) {
    return (
      <Screen testId="order-detail-page">
        <TopBar title="Pedido" onBack={() => navigate(pathTo('orders', slug, sessionId))} />
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </Screen>
    )
  }

  // An id that resolves to nothing is a wrong URL — or an order belonging to
  // another session, which the server answers the same way. Saying so beats
  // showing somebody a different order than the one they asked for.
  if (!order) {
    return (
      <Screen testId="order-detail-page">
        <TopBar title="Pedido" onBack={() => navigate(pathTo('orders', slug, sessionId))} />
        <Box sx={{ p: 2.5 }}>
          <Alert severity="warning">
            {orderError
              ? `No pudimos cargar el pedido. ${orderError.message}`
              : `No encontramos el pedido ${orderId}.`}
          </Alert>
          <Button
            variant="contained"
            sx={{ mt: 2 }}
            onClick={() => navigate(pathTo('orders', slug, sessionId))}
          >
            Ver mis pedidos
          </Button>
        </Box>
      </Screen>
    )
  }

  const totalText = order.totalText

  return (
    <Screen testId="order-detail-page">
      <TopBar
        title={order.reference}
        subtitle={order.dateText}
        onBack={() => navigate(pathTo('orders', slug, sessionId))}
        trailing={<OrderStatusBadge status={order.status} />}
      />

      <Box sx={{ flex: 1, px: 2.5, pt: 1, pb: 2.5 }}>
        <Card variant="outlined" sx={{ p: 1.75 }}>
          <Stack spacing={1.25}>
            <Row
              label="Entrega"
              value={order.deliveryToken ? deliveryOptionLabel(order.deliveryToken) : '—'}
            />
            <Row label="Pago" value={paymentMethodLabel(order.paymentToken)} />
            <Row label="Distribuidor" value={distributor.name} />
          </Stack>
        </Card>

        <Typography variant="overline" color="text.secondary" display="block" sx={{ mt: 2.5 }}>
          Resumen · {itemCountLabel(order.units)}
        </Typography>

        <Stack spacing={1.25} sx={{ mt: 1 }}>
          {order.lines.map((line) => (
            <Card key={line.sku} variant="outlined" sx={{ px: 1.75, py: 1.5 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={1.25}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {line.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {line.quantity} cajas
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight={700} sx={{ flex: 'none' }}>
                  {line.amountText}
                </Typography>
              </Stack>
            </Card>
          ))}
        </Stack>

        {totalText && (
          <Stack direction="row" justifyContent="space-between" sx={{ mt: 2, px: 0.25 }}>
            <Typography variant="h5" component="p">
              Total
            </Typography>
            <Typography variant="h5" component="p">
              {totalText}
            </Typography>
          </Stack>
        )}

        {/* Said after the check but before anything is added: the first tap
            stops here, and it is the second one that commits — never an alert
            explaining a cart that is already short. */}
        {unavailable.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {available.length === 0
              ? 'Ninguno de los productos de este pedido está disponible ahora: '
              : 'No podemos repetir todo el pedido: '}
            {unavailableLinesText(unavailable)}.
          </Alert>
        )}

        {/* Without the catalog there is no way to know what is still sellable,
            so nothing was added; the button stays armed for a retry. */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error.message}
          </Alert>
        )}

        {cartError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {cartError.message}
          </Alert>
        )}
      </Box>

      <ActionBar>
        <Button
          fullWidth
          size="large"
          variant="contained"
          // Withheld only once the check *proved* nothing is sellable. Before
          // the check, tapping is what triggers it; on a failed check it stays
          // armed as the retry.
          disabled={loading || (checked !== null && available.length === 0)}
          onClick={reorder}
        >
          {loading
            ? 'Revisando disponibilidad…'
            : unavailable.length > 0 && available.length > 0
              ? 'Agregar lo disponible'
              : 'Pedir de nuevo'}
        </Button>
        {(order.status === 'Cancelado' || order.status === 'Con error') && (
          <Typography
            variant="caption"
            display="block"
            sx={{ mt: 1, textAlign: 'center', color: YALO_SEMANTIC.fg3 }}
          >
            Este pedido fue cancelado.
          </Typography>
        )}
      </ActionBar>
    </Screen>
  )
}
